import WebSocketClient from './WebSocketClient';
import { IMessage } from '@stomp/stompjs';

/*
 * Represents a client dedicated to a specific websocket channel.
 * This class simplifies interactions with a single channel by avoiding
 * to repeatedly pass the channel name for every operation.
 */
export class WebSocketChannelClient {
  private channelName: string;
  private handlers: Map<string, Function>;

  constructor(channelName: string) {
    this.channelName = channelName;
    this.handlers = new Map();
  }

  // Internal function to handle incoming messages and delegate to the correct handler.
  private handleIncomingMessage(channelId: string, message: string | IMessage) {
    const handler = this.handlers.get(channelId);
    if (handler) {
      handler(message);
    }
  }

  // Subscribe to a channel with a simple string message handler.
  public subscribe(channelId: string, handler: (s: string) => void) {
    if (!this.handlers.has(channelId)) {
      WebSocketClient.subscribe(this.channelName, channelId, (msg) => this.handleIncomingMessage(channelId, msg));
    }
    this.handlers.set(channelId, handler);
  }

  // Subscribe to a channel with a raw message handler, providing access to IMessage details.
  public subscribeRaw(channelId: string, handler: (s: IMessage) => void) {
    if (!this.handlers.has(channelId)) {
      WebSocketClient.subscribeRaw(this.channelName, channelId, (msg) => this.handleIncomingMessage(channelId, msg));
    }
    this.handlers.set(channelId, handler);
  }

  // Unsubscribe from the channel.
  public unsubscribe(channelId: string) {
    this.handlers.delete(channelId);
    WebSocketClient.unsubscribe(this.channelName, channelId);
  }

  // Send a message to the channel.
  public sendMessage(channelId: string, content: string) {
    WebSocketClient.sendMessage(this.channelName, channelId, content);
  }

  // Update the handler for a given channel. For example, when React state changes and we need the handler to have access to the latest state.
  public updateHandler(channelId: string, newHandler: (s: any) => void) {
    if (this.handlers.has(channelId)) {
      this.handlers.set(channelId, newHandler);
    } else {
      console.warn(`No active handler found for channel ID: ${channelId}`);
    }
  }
}
